import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";

import { ChatMessage as ChatMessageComponent } from "@/components/ai-chat/chat-message";
import { cn } from "@/lib/utils";
import { ChatMessageRating, ChatMessageRole, ChatMessage as ChatMessageType } from "@/types/chatbot.types";
import { fadeInUp } from "@/utils/animations";

const PINNED_TO_BOTTOM_THRESHOLD_PX = 80;

type ChatMessagesProps = {
    messages: ChatMessageType[];
    isLoading?: boolean;
    className?: string;
    onRate?: (params: { message: ChatMessageType; rating?: ChatMessageRating }) => void;
    onRequestTool?: (message: ChatMessageType) => void;
    requestingToolMessageId?: string;
};

export function ChatMessages({
    messages,
    isLoading = false,
    className,
    onRate,
    onRequestTool,
    requestingToolMessageId,
}: ChatMessagesProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isPinnedToBottomRef = useRef(true);

    // Follow the reply as it grows, unless the user scrolled up to read
    useEffect(() => {
        const container = scrollContainerRef.current;
        const content = contentRef.current;
        if (!container || !content) return;

        const handleScroll = () => {
            const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            isPinnedToBottomRef.current = distanceFromBottom < PINNED_TO_BOTTOM_THRESHOLD_PX;
        };
        const observer = new ResizeObserver(() => {
            if (isPinnedToBottomRef.current) container.scrollTop = container.scrollHeight;
        });

        container.addEventListener("scroll", handleScroll, { passive: true });
        observer.observe(content);
        return () => {
            container.removeEventListener("scroll", handleScroll);
            observer.disconnect();
        };
    }, []);

    // A new message always brings the view back down
    useEffect(() => {
        isPinnedToBottomRef.current = true;
        scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: "smooth" });
    }, [messages.length]);

    return (
        <div ref={scrollContainerRef} className={cn("h-full min-w-0 overflow-x-hidden overflow-y-auto p-4", className)}>
            <div ref={contentRef} className="space-y-4">
                <AnimatePresence initial={false}>
                    {messages.map((message, index) => {
                        const isLastMessage = index === messages.length - 1;
                        return (
                            <motion.div key={message.id} {...fadeInUp} transition={{ duration: 0.2 }}>
                                <ChatMessageComponent
                                    message={message}
                                    isStreaming={
                                        isLoading && isLastMessage && message.role === ChatMessageRole.ASSISTANT
                                    }
                                    onRate={isLoading && isLastMessage ? undefined : onRate}
                                    onRequestTool={isLoading && isLastMessage ? undefined : onRequestTool}
                                    isRequestingTool={message.id === requestingToolMessageId}
                                />
                            </motion.div>
                        );
                    })}
                </AnimatePresence>

                {/* Waiting for the first words of the reply */}
                {isLoading && messages[messages.length - 1]?.role !== ChatMessageRole.ASSISTANT && (
                    <motion.div {...fadeInUp} className="flex items-start gap-3">
                        <div className="bg-gray-100 dark:bg-white/10 rounded-2xl rounded-tl-md px-4 py-3">
                            <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-100" />
                                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce delay-200" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
